import {
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  Popconfirm,
  Select,
  Table,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import React, { useCallback, useEffect, useState } from "react";
import { errorMessage, repositoryLabel } from "../Settings/ParamTable";
import {
  fetchEnvironmentFeatures,
  fetchFeatures,
  linkFeature,
  unlinkFeature,
  updateFeatureLink,
} from "../../redux/actions/featureActions";

import PropTypes from "prop-types";
import moment from "moment";

const KEY_PATTERN = /^[\x20-\x7E]+$/;

/*
 * BI-680 : liaison d'une fonctionnalite du catalogue (Settings > Fonctionnalites)
 * a l'environnement. Nom/type/modules en lecture seule ; la cle n'est jamais
 * renvoyee par l'API ni pre-remplie.
 */
function FeatureSection({ finessId }) {
  const [links, setLinks] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // liaison en edition
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    if (finessId === undefined || finessId === null) return;
    try {
      setLinks(await fetchEnvironmentFeatures(finessId));
    } catch (e) {
      message.error(errorMessage(e));
    }
    setLoaded(true);
  }, [finessId]);

  const loadCatalogue = async () => {
    try {
      const r = await fetchFeatures();
      setCatalogue(r.features);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  useEffect(() => {
    setLoaded(false);
    load();
  }, [load]);

  useEffect(() => {
    loadCatalogue();
  }, []);

  const closeForm = () => {
    form.resetFields();
    setEditing(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    let v;
    try {
      v = await form.validateFields();
    } catch (e) {
      return;
    }
    const payload = {
      expiration_date: v.expiration_date
        ? v.expiration_date.format("YYYY-MM-DD")
        : null,
    };
    // cle uniquement si saisie (vide = inchangee en edition)
    if (typeof v.key === "string" && v.key.trim()) payload.key = v.key.trim();
    try {
      if (editing) {
        await updateFeatureLink(finessId, editing.oid, payload);
        message.success("Liaison modifiée");
      } else {
        await linkFeature(finessId, { ...payload, feature_id: v.feature_id });
        message.success("Fonctionnalité liée");
      }
      closeForm();
      load();
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const handleEdit = (link) => {
    setEditing(link);
    setShowForm(true);
    // le formulaire peut ne pas etre monte : on pre-remplit apres le rendu
    setTimeout(() => {
      form.resetFields();
      form.setFieldsValue({
        key: "", // jamais renvoyee par l'API
        expiration_date: link.expiration_date
          ? moment(link.expiration_date)
          : null,
      });
    }, 0);
  };

  const handleDelete = async (link) => {
    try {
      await unlinkFeature(finessId, link.oid);
      message.success("Liaison retirée");
      load();
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const columns = [
    { title: "Nom", dataIndex: "name", key: "name" },
    {
      title: "Modules",
      key: "modules",
      render: (t, f) =>
        (Array.isArray(f.modules) ? f.modules : [])
          .map((m) => m.name)
          .join(", "),
    },
    {
      title: "Repository",
      key: "repository",
      render: (t, f) => (f.repository ? repositoryLabel(f.repository) : ""),
    },
    { title: "Provider", dataIndex: "provider_type", key: "provider_type" },
    {
      title: "Clé configurée",
      key: "key_configured",
      render: (t, f) => (f.key_configured ? "Oui" : "Non"),
    },
    {
      title: "Expiration",
      dataIndex: "expiration_date",
      key: "expiration_date",
      render: (d) => (d ? moment(d).format("DD/MM/YYYY") : ""),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (t, f) => (
        <>
          <Button
            aria-label="Modifier"
            title="Modifier"
            style={{ marginRight: 8 }}
            onClick={() => handleEdit(f)}
          >
            <EditOutlined />
          </Button>
          <Popconfirm
            title="Retirer cette fonctionnalité de l'environnement ?"
            okText="retirer"
            cancelText="Annuler"
            onConfirm={() => handleDelete(f)}
          >
            <Button danger aria-label="Retirer" title="Retirer">
              <DeleteOutlined />
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  const linkedIds = new Set(links.map((l) => l.feature_id));
  const options = catalogue
    .filter((f) => !linkedIds.has(f.oid))
    .map((f) => ({ value: f.oid, label: f.name }));

  const renderKeyField = (type, configured) => {
    if (type === "API") {
      return (
        <Form.Item
          name="key"
          label="Clé"
          extra={configured ? "Clé configurée" : undefined}
          rules={[
            {
              required: !configured,
              whitespace: true,
              message: "La clé est obligatoire pour une fonctionnalité API",
            },
            {
              validator: (_, value) =>
                typeof value === "string" &&
                value.trim() &&
                !KEY_PATTERN.test(value.trim())
                  ? Promise.reject(
                      new Error(
                        "La clé ne doit contenir que des caractères ASCII imprimables",
                      ),
                    )
                  : Promise.resolve(),
            },
          ]}
        >
          <Input.Password
            autoComplete="new-password"
            placeholder={configured ? "laisser vide pour conserver" : undefined}
          />
        </Form.Item>
      );
    }
    if (type === "licence") {
      return (
        <Form.Item label="Clé">
          <span>Clé générée automatiquement</span>
        </Form.Item>
      );
    }
    return null;
  };

  return (
    <div className="feature-section" style={{ marginTop: 24 }}>
      <span style={{ fontSize: "1.5em", fontWeight: 500, marginRight: 12 }}>
        Fonctionnalités
      </span>
      <Button
        type="primary"
        onClick={() => {
          if (showForm) closeForm();
          else setShowForm(true);
        }}
      >
        <PlusCircleOutlined /> Ajouter
      </Button>
      <Divider />
      {showForm && (
        <Form form={form} layout="vertical" style={{ marginBottom: 16 }}>
          {editing ? (
            <>
              <div style={{ fontWeight: 500, marginBottom: 12 }}>
                {`Modifier la fonctionnalité : ${editing.name}`}
              </div>
              <div style={{ marginBottom: 12, color: "rgba(0,0,0,.45)" }}>
                {`Type : ${editing.provider_type || ""} - Modules : ${(
                  Array.isArray(editing.modules) ? editing.modules : []
                )
                  .map((m) => m.name)
                  .join(", ")}`}
              </div>
              {renderKeyField(editing.provider_type, editing.key_configured)}
            </>
          ) : (
            <>
              <div style={{ fontWeight: 500, marginBottom: 12 }}>
                Lier une fonctionnalité
              </div>
              <Form.Item
                name="feature_id"
                label="Fonctionnalité"
                rules={[{ required: true, message: "Champ requis" }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={options}
                  placeholder="Choisir dans le catalogue"
                />
              </Form.Item>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const f = catalogue.find(
                    (c) => c.oid === getFieldValue("feature_id"),
                  );
                  return f ? renderKeyField(f.provider_type, false) : null;
                }}
              </Form.Item>
            </>
          )}
          <Form.Item name="expiration_date" label="Date d'expiration">
            <DatePicker />
          </Form.Item>
          <Button type="primary" onClick={handleSubmit}>
            {editing ? "Enregistrer" : "Lier"}
          </Button>
          <Button style={{ marginLeft: 8 }} onClick={closeForm}>
            Annuler
          </Button>
        </Form>
      )}
      <Table
        rowKey="oid"
        columns={columns}
        pagination={false}
        loading={!loaded}
        dataSource={links}
      />
    </div>
  );
}

FeatureSection.propTypes = {
  finessId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default FeatureSection;
